import { QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { I18nextProvider } from 'react-i18next';

import { ApiError } from '@/shared/api/client';
import type { Me } from '@/shared/api/types';
import { useAuthBootstrap, useAuthStore } from '@/shared/hooks/useAuth';
import i18n from '@/shared/i18n';
import { Toaster } from '@/shared/ui';

import { ErrorBoundary } from './ErrorBoundary';

function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        retry: 1,
        // TMA сворачивается/разворачивается постоянно; refetch на фокус
        // означал бы шквал запросов при каждом переключении приложения.
        refetchOnWindowFocus: false,
        refetchOnReconnect: true,
      },
      mutations: {
        // Мутация могла примениться до обрыва — повтор недопустим.
        retry: 0,
      },
    },
    queryCache: new QueryCache({
      onError: (error) => {
        // 401 обрабатывает клиент (refresh + auth:logout), шуметь не нужно.
        if (error instanceof ApiError && error.status === 401) return;
      },
      onSuccess: (data, query) => {
        // Синхронизируем useAuth zustand store с qk.me из React Query
        if (query.queryKey[0] === 'client' && query.queryKey[1] === 'me') {
          useAuthStore.getState().setMe(data as Me);
        }
      },
    }),
  });
}

function Bootstrap({ children }: { children: ReactNode }): ReactNode {
  useAuthBootstrap();
  return children;
}

export function Providers({ children }: { children: ReactNode }): React.ReactElement {
  // QueryClient создаётся один раз на жизнь приложения: пересоздание в теле
  // компонента обнуляло бы кэш на каждом ререндере.
  const [queryClient] = useState(createQueryClient);

  return (
    <ErrorBoundary>
      <I18nextProvider i18n={i18n}>
        <QueryClientProvider client={queryClient}>
          <Bootstrap>{children}</Bootstrap>
          <Toaster />
        </QueryClientProvider>
      </I18nextProvider>
    </ErrorBoundary>
  );
}
